/**
 * The game: the store, its events, where I am, and the clock. Everything else in this layer is a
 * function over it. main.ts creates one and hands it to the world, the input, the UI and the net.
 */
import { RULES_VERSION } from '@bramvasterd/terrella-rules';

import { type Clock } from '@core/clock';
import { createEmitter, type Emitter } from '@core/events';
import { quat, quatCopy, vecSet } from '@core/quat';
import type { Storage } from '@platform/storage';

import { createLocal, type Local } from './player/local';
import { createGameStore, initialState, type GameStore } from './store';
import type { GameEvents, Me, WorldInfo } from './types';

export interface GameDeps {
  clock: Clock;
  storage: Storage;
  /** performance.now(), injectable for tests. */
  now: () => number;
}

export interface Game {
  store: GameStore;
  events: Emitter<GameEvents>;
  local: Local;
  clock: Clock;
  deps: GameDeps;
  /** The rules version the app was built with. */
  rulesVersion: number;
}

export function createGame(deps: GameDeps): Game {
  return {
    store: createGameStore(),
    events: createEmitter<GameEvents>(),
    local: createLocal(),
    clock: deps.clock,
    deps,
    rulesVersion: RULES_VERSION,
  };
}

export const intentKey = (worldId: string): string => `intent:${worldId}`;
export const seqKey = (worldId: string): string => `seq:${worldId}`;

export function setMe(game: Game, me: Me | null): void {
  game.store.set({ me });
}

/** Enter a world: the planet arrives with the connection's welcome. */
export async function enterWorld(game: Game, world: WorldInfo): Promise<void> {
  const stored = Number((await game.deps.storage.get(intentKey(world.id))) ?? 0);
  const seq = Number((await game.deps.storage.get(seqKey(world.id))) ?? 0);
  const { me } = game.store.get();
  game.store.set({
    ...initialState(),
    me,
    world,
    connection: 'connecting',
    nextIntentId: Math.max(1, stored + 1),
    seq: Number.isFinite(seq) ? seq : 0,
  });
  // where I stand comes with the first welcome: on the hub (player/arrival.ts)
  quatCopy(game.local.q, quat());
  vecSet(game.local.heading, 0, 0, -1);
  game.local.walk = null;
  game.local.viewTarget = 0;
  game.events.emit('world', world);
}

export function leaveWorld(game: Game): void {
  const me = game.store.get().me;
  game.store.set({ ...initialState(), me });
  game.events.emit('world', null);
}
